import { CONTACT_EMAIL } from "../../data/constants";
import { appsEn } from "../../data/apps";
import type { UserInfo } from "../types";

export const user: UserInfo = {
	main: {
		title: "Shimomura Kazumasa",
		name: "Kazumasa Shimomura",
		email: CONTACT_EMAIL,
		logo: "../logo.png",
	},

	socials: {
		twitter: "https://twitter.com/_kzumu",
		github: "https://github.com/s2mr",
		linkedin: "https://www.linkedin.com/in/s2mr/",
		facebook: "https://www.facebook.com/kaz.s2mr/",
		reddit: "https://www.reddit.com/user/kzumu"
	},

	homepage: {
		name: "Kazumasa Shimomura",
		hurigana: "Shimomura Kazumasa",
		description: `A software engineer specializing in iOS development.
At CyberAgent, I led the launch and operation of the large-scale live streaming app "CL".
With multiple 0-to-1 iOS app development experiences, I excel at designing applications with a focus on user experience.
Since 2017, I have been continuously developing personal projects, with over 20 apps published and operated.`,
	},

	about: {
		title: "Building products end to end, with iOS at the core.",
		description:
			"At CyberAgent, I work on iOS application development and architecture design for large-scale distribution platforms. I specialize in high-quality application development using cutting-edge technologies such as modular architecture, GraphQL, and AI integration. In personal development, I have published over 20 apps, many of which are contributed as open source.",
	},

	outputs: {
		title: "Outputs",
		description:
			"I publish articles on knowledge and practices gained from development.",
	},

	techStack: {
		title: "Tech Stack & Skills",
		description: "Technology stack and skill set used in work and personal development",
		languages: [
			{
				name: "Swift",
				level: "Expert",
				yearsOfExperience: "7+",
				icon: "🍎",
				color: "#FA7343",
				description: "Primary language for iOS development with 7+ years of professional experience. Building high-quality applications using Swift Concurrency and Combine.",
				frameworks: [
					"SwiftUI",
					"UIKit",
					"Combine",
					"Swift Concurrency (async/await)",
					"MessageKit",
					"WidgetKit"
				],
				tools: [
					"Xcode",
					"Tuist",
					"Swift Package Manager",
					"CocoaPods",
					"Carthage",
					"Scipio",
					"Fastlane"
				],
				libraries: [
					"AVFoundation",
					"CoreData",
					"Realm",
					"Firebase SDK",
					"Alamofire",
					"Lottie",
					"SwiftSoup"
				],
				testing: ["XCTest", "XCUITest"],
				architecture: ["MVVM", "Flux/Redux", "Clean Architecture", "Modular Architecture", "Repository Pattern"]
			},
			{
				name: "TypeScript",
				level: "Advanced",
				yearsOfExperience: "5+",
				icon: "📘",
				color: "#3178C6",
				description: "Used for backend and web development. Type-safe development with TypeSpec/OpenAPI, serverless development on Cloud Run/Cloud Functions/AWS Lambda.",
				frameworks: [
					"Node.js",
					"Express.js",
					"Fastify",
					"React",
					"Next.js"
				],
				tools: [
					"TypeSpec/OpenAPI",
					"Prisma",
					"ESLint",
					"Prettier",
					"ts-node",
					"pnpm"
				],
				libraries: [
					"GraphQL (PostGraphile)",
					"Zod",
					"Axios",
					"LangChain",
					"AI SDK (Vercel)",
					"Firebase Admin SDK",
					"Firecrawl"
				],
				testing: ["Jest", "Supertest", "ts-jest"],
				architecture: ["Microservices", "Repository Pattern", "Clean Architecture", "Serverless"]
			},
			{
				name: "Dart",
				level: "Intermediate",
				yearsOfExperience: "2+",
				icon: "🎯",
				color: "#0175C2",
				description: "Used for cross-platform application development with Flutter.",
				frameworks: [
					"Flutter"
				],
				tools: [
					"Pub",
					"Flutter DevTools"
				],
				libraries: [
					"Provider",
					"Riverpod",
					"Freezed"
				],
				testing: ["Flutter Test", "Mockito"],
				architecture: ["MVVM", "BLoC Pattern"]
			},
			{
				name: "Go",
				level: "Intermediate",
				yearsOfExperience: "2+",
				icon: "🐹",
				color: "#00ADD8",
				description: "Used for microservices and CLI tool development.",
				frameworks: [
					"gRPC",
					"Echo"
				],
				tools: [
					"Go Modules",
					"Task"
				],
				libraries: [
					"Protocol Buffers",
					"GORM"
				],
				testing: ["Testing Package", "Testify"],
				architecture: ["Microservices", "Clean Architecture"]
			}
		],
		platforms: [
			{
				name: "iOS",
				icon: "📱",
				technologies: ["Swift", "SwiftUI", "Combine", "Swift Concurrency", "WidgetKit", "Tuist"]
			},
			{
				name: "Backend",
				icon: "⚙️",
				technologies: ["Node.js", "TypeScript", "Express.js", "GraphQL", "Prisma", "TypeSpec"]
			},
			{
				name: "Web",
				icon: "🌐",
				technologies: ["React", "Next.js", "TypeScript", "Styled Components"]
			},
			{
				name: "GCP",
				icon: "☁️",
				technologies: ["Cloud Run", "Cloud Functions", "Cloud Storage", "Cloud Tasks", "Cloud SQL", "Cloud Build"]
			},
			{
				name: "AWS",
				icon: "🔶",
				technologies: ["Lambda", "CDK", "Serverless Express"]
			},
			{
				name: "AI/ML",
				icon: "🤖",
				technologies: ["Google Gemini API", "OpenAI API", "LangChain", "Cloud TTS", "Firecrawl"]
			}
		],
		databases: [
			{ name: "Realm", type: "Mobile Database", icon: "📦" },
			{ name: "PostgreSQL", type: "Relational DB", icon: "🐘" },
			{ name: "Firestore", type: "NoSQL", icon: "🔥" },
			{ name: "Cloud SQL", type: "Managed SQL", icon: "☁️" }
		],
		devOps: [
			"Docker",
			"Cloud Build",
			"GitHub Actions",
			"Fastlane",
			"Taskfile",
			"AWS CDK",
			"CI/CD"
		],
		other: [
			"HLS Streaming",
			"FFmpeg",
			"WebSocket",
			"Push Notifications",
			"In-App Purchase",
			"App Store Connect API",
			"Firebase (Crashlytics, Analytics, Remote Config, App Check)",
			"gRPC",
			"Protocol Buffers"
		]
	},

	projects: {
		tools: {
			title: "Open Source Tools & Libraries",
			description: "Developed and published tools aimed at improving iOS development productivity.",
			items: [
				{
					title: "L10nLint",
					description: "A Swift-based localization linter. Automates quality control of Localizable.strings and improves development efficiency for multilingual apps.",
					logo: null,
					linkText: "View Project",
					link: "https://github.com/s2mr/L10nLint",
				},

				{
					title: "xc",
					description:
						"Xcode version management CLI tool. Easily switch between multiple Xcode versions and support smooth transitions between projects.",
					logo: null,
					linkText: "View Project",
					link: "https://github.com/s2mr/xc",
				},
			],
		},

		app: {
			title: "Apps",
			description: "Personal products currently or previously released, excluding learning and exam-preparation apps. The catalog spans iOS, macOS, and cross-platform products.",
			items: appsEn,
		},

		subwork: {
			title: "External Projects & Consulting",
			description: "Technical advisory and support for 0-to-1 phase launches in external development projects.",
			items: [
				{
					title: "MUFASA Technology",
					terms: "2019 - Present",
					description: "Technical advisor. Development and maintenance of iOS app \"Follop\", development of Shopify inventory integration system for luxury brand EC sites. Implemented real-time inventory management system that automatically syncs inventory from multiple warehouses using Node.js + Shopify API.",
					logo: "",
					linkText: "View Project",
					link: "https://hidetokyo.jp/",
				},
				{
					title: "Caravelle Inc.",
					terms: "2022/05 - Present",
					description: "New iOS app development. Implementation with modern architecture using SwiftUI + Combine. Led product development in 0-to-1 phase.",
					logo: "",
					linkText: "View Company",
					link: "https://caravelle-inc.tokyo/",
				},
				{
					title: "Replive Inc.",
					terms: "2023/05 - 2024/05",
					description: "Technical advisor. Technology selection, architecture design, new business launch support. Provided full-stack technical support for iOS/backend.",
					logo: "",
					linkText: "View Company",
					link: "https://replive.jp/",
				},
				{
					title: "Motoyui Inc. - RecHub",
					terms: "2022",
					description:
						"New development of recruitment-focused video sharing platform. In charge of frontend and backend design and implementation.",
					logo: "",
					linkText: "View Company",
					link: "https://motoyui.com/",
				},
				{
					title: "Ricecurry Inc. - Quick Korean Quiz",
					terms: "2021",
					description:
						"0-to-1 development of real-time multiplayer quiz app. Implemented matching function using Firebase Realtime Database and AI bot battle system.",
					logo: "https://is1-ssl.mzstatic.com/image/thumb/Purple114/v4/d9/8c/14/d98c142b-ae2b-6628-b6db-a0c8f1cb047b/AppIcon-0-0-1x_U007emarketing-0-0-0-6-0-0-sRGB-0-0-0-GLES2_U002c0-512MB-85-220-0-0.png/460x0w.webp",
					linkText: "View on App Store",
					link: "https://apps.apple.com/jp/app/id1544608791",
				},
				{
					title: "Arriv Inc.",
					terms: "2020",
					description: "New iOS application development. Consistently handled from UI/UX design to release.",
					logo: "",
					linkText: "",
					link: "",
				},
				{
					title: "Yenom Inc. - Bitcoin Wallet",
					terms: "2018",
					description:
						"Maintenance and feature additions for Bitcoin wallet app and BitcoinKit library. Acquired cryptocurrency-related technical knowledge.",
					logo: "",
					linkText: "View on GitHub",
					link: "https://github.com/yenom",
				},
				{
					title: "Mamorio Inc.",
					terms: "2017",
					description: "Development support for IoT device integration app. Gained experience in BLE communication implementation.",
					logo: "",
					linkText: "",
					link: "",
				},
				{
					title: "PicApp Inc. - Teller",
					terms: "2017",
					description: "Summer internship. Worked on feature development for Teller.",
					logo: "",
					linkText: "",
					link: "",
				},
				{
					title: "Wantedly Inc.",
					terms: "2017",
					description: "Summer internship. Participated in application development with iOS development team.",
					logo: "",
					linkText: "",
					link: "",
				},
				{
					title: "Milldea LLC.",
					terms: "2016",
					description: "iOS development part-time job. First step in professional development experience.",
					logo: "",
					linkText: "",
					link: "",
				}
			],
		},
	},

	talks: {
		title: "Conference Talks",
		description: "Technical conference presentations and talks",
		items: [
			{
				title: "Safe Management of Localization: 7 Languages, Over 1200 Lines Per File",
				event: "iOSDC Japan 2023",
				date: "2023/09/02",
				link: "https://fortee.jp/iosdc-japan-2023/proposal/6c6d45d4-376a-42c6-8210-cca3c329025a",
				description: "Presented the development and operation of 'L10nLint', a static analysis tool for localization files. Introduced methods to automate quality control for localization files supporting 7 languages with over 1200 lines.",
			},
		],
	},
};
